/** @jsxImportSource @emotion/react */
'use memo'
import css from '@emotion/css/macro'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Spin } from 'antd'
import { LinkOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { useSelector } from 'src/hooks/useRedux'
import { useUrlManager } from 'src/hooks/useUrlManager'
import RosterSelectNew, {
  useRosterSelect,
} from 'src/components/List/RosterSelectNew'
import SendInvoiceModal from 'src/components/SendInvoiceModal'
import { useBillingContext } from './context/BillingContext'
import { useUIContext } from './context/UIContext'
import BillingCards from './components/BillingCards'
import BillingTable from './components/BillingTable'

interface TeamBillingPageProps {
  teamId: string
  canEdit: boolean
}

const TeamBillingPage = ({ teamId, canEdit }: TeamBillingPageProps) => {
  const { setParam } = useUrlManager()
  const me = useSelector((state: any) => state.auth.user)

  const {
    loading: loadingRosters,
    rosters,
    selectedRosterId,
    setSelectedRosterId,
    loadRosters,
  } = useRosterSelect(teamId)

  const { loading, billingLoading, billingItems, error, fetchBilling } =
    useBillingContext()

  const { sendInvoiceModalOpen, sendInvoiceBilling, closeSendInvoiceModal } =
    useUIContext()

  // Fetch billing when roster is selected
  useEffect(() => {
    if (selectedRosterId) {
      fetchBilling(selectedRosterId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRosterId])

  const shouldDisplayBillingPayment = (() => {
    if (
      billingItems &&
      billingItems.length > 0 &&
      me?.id === billingItems[0]?.user_id
    ) {
      return true
    }
    if (canEdit) {
      return true
    }
    return false
  })()

  const selectedRoster: any =
    rosters.find((r: any) => r.id === selectedRosterId) || rosters[0]

  const billingUserEmail = me?.email || ''
  const billingUserName = me?.name_full || me?.name || ''

  if (loadingRosters) {
    return (
      <div
        css={css`
          margin-top: 24px;
          display: flex;
          justify-content: center;
          align-items: center;
        `}
      >
        <Spin size='small' />
      </div>
    )
  }

  if (!me) {
    return (
      <div
        css={css`
          margin-top: 40px;
        `}
      >
        <p>We cannot show the financial details right now, try again later.</p>
      </div>
    )
  }

  if (rosters.length === 0) {
    return (
      <div
        css={css`
          margin-top: 40px;
        `}
      >
        <p>The team has no related charges.</p>
      </div>
    )
  }

  if (error && !loading && !billingLoading) {
    return (
      <div
        css={css`
          margin-top: 40px;
        `}
      >
        <p>We cannot show the financial details right now, try again later.</p>
      </div>
    )
  }

  return (
    <section
      css={css`
        height: 80vh;
        padding-bottom: 100px;
      `}
    >
      {/* Roster selector row */}
      <div
        css={css`
          display: flex;
          align-items: center;
          padding: 24px 0;
          gap: 20px;
        `}
      >
        <RosterSelectNew
          selectedRosterId={selectedRosterId}
          setSelectedRosterId={setSelectedRosterId}
          rosters={rosters}
          loadRosters={loadRosters}
          teamId={teamId}
          teamName=''
          permission={{ update: canEdit, admin: false }}
          setParam={setParam as any}
        />
        {rosters.length > 0 && selectedRoster && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              height: 40px;
              gap: 8px;
              margin-bottom: 8px;
            `}
          >
            <Link
              to={`/schedule/${selectedRoster?.schedule_uid}`}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <p
                css={css`
                  font-size: 24px;
                  font-weight: 700;
                  font-family: ${isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                  line-height: 32px;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                `}
              >
                {selectedRoster?.schedule_name}
              </p>
              <LinkOutlined
                css={css`
                  font-size: 24px;
                  color: ${colors.PRIMARY};
                `}
              />
            </Link>
            <p
              css={css`
                color: ${colors.NEUTRAL_50};
              `}
            >
              {selectedRoster?.division_name}
            </p>
          </div>
        )}
      </div>

      {/* Content */}
      {loading || billingLoading ? (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            margin-top: 24px;
          `}
        >
          <Spin size='small' />
        </div>
      ) : (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 24px;
          `}
        >
          <BillingCards />
          <BillingTable
            shouldDisplayBillingPayment={shouldDisplayBillingPayment}
          />
        </div>
      )}

      <SendInvoiceModal
        isOpen={sendInvoiceModalOpen}
        onClose={closeSendInvoiceModal}
        defaultEmail={billingUserEmail}
        defaultName={billingUserName}
        billing={sendInvoiceBilling}
      />
    </section>
  )
}

export default TeamBillingPage
